import {useEffect} from 'react';
import type {ReactNode} from 'react';
import type {SelectOption} from '../../lib/selector-option';
import {flattenGroupedOptions} from '../_select-internals/utils';
import type {
  SelectChangeMeta,
  SelectGroupedOption,
  SelectValue,
} from '../_select-internals';

type Params<T, V extends SelectValue, L extends ReactNode> = {
  value: SelectOption<T, V, L> | null | undefined;
  options: SelectGroupedOption<T, V, L>[] | undefined;
  onChange?: (option: SelectOption<T, V, L> | null, meta?: SelectChangeMeta) => void;
  enabled?: boolean;
};

export function useSetCorrectOptionIfThereIsOnlyValue<
  T,
  V extends SelectValue,
  L extends ReactNode,
>({value, options, onChange, enabled = true}: Params<T, V, L>) {
  useEffect(() => {
    if (!enabled || !value || !onChange || !options?.length) return;
    if (value.value === undefined || value.value === null) return;
    if (value.label !== 'NONE' && value.label !== '') return;

    const validOption = flattenGroupedOptions(options).find(
      option => option.value === value.value,
    );
    if (!validOption) return;

    onChange({...validOption, isPrefilled: true}, {action: 'select'});
  }, [enabled, onChange, options, value]);
}
